<template>
  <!-- Data View -->
  <div
    class="tw:flex tw:flex-col tw:rounded-xl tw:min-h-full tw:border tw:border-gray-300"
    v-if="currentView === 'data'"
    :class="{ 'tw:grayscale': noFilterData || (!hasRateConnect && (id === 'revenue' || id === 'bookings')) }"
  >
    <!-- Header Section -->
    <div class="tw:bg-gray-100 tw:p-4 tw:rounded-t-xl tw:border-b tw:border-gray-300">
      <div class="tw:mb-5 tw:flex tw:justify-between tw:items-start">
        <div class="tw:flex tw:flex-row tw:items-center tw:gap-x-2">
          <div class="tw:rounded-full tw:p-3 tw:bg-decorative-lightblue tw:flex-shrink-0">
            <img :src="icons[id]" :alt="title" class="tw:h-6 tw:w-6 tw:object-contain" />
          </div>
          <div>
            <div class="tw:flex tw:items-center">
              <div class="tw:flex-1 tw:min-w-0">
                <div class="tw:flex tw:items-center tw:gap-x-1">
                  <p class="tw:font-bold tw:text-lg">{{ $t(title) }}</p>
                  <UiTooltip tooltip-position="top" :label="$t(hint)" class="tw:cursor-pointer">
                    <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[16px] tw:w-[16px]" />
                  </UiTooltip>
                </div>

                <i18n-t :keypath="comparisonText" tag="p" v-if="progression >= 0 || noFilterData" class="tw:text-sm">
                  <template #Percent>
                    <span class="tw:font-bold">↑ {{ noFilterData ? '-' : formatPercentage(progression, 0, 2) }}</span>
                  </template>
                </i18n-t>

                <i18n-t :keypath="comparisonText" tag="p" v-if="progression < 0 && !noFilterData" class="tw:text-sm">
                  <template #Percent>
                    <span class="tw:font-bold">↓ {{ formatPercentage(progression, 0, 2) }}</span>
                  </template>
                </i18n-t>
              </div>
            </div>
          </div>
        </div>
        <div>
          <button
            v-if="hasRateConnect && !noFilterData && id === 'impressions'"
            class="tw:hover:cursor-pointer tw:rounded tw:hover:bg-gray-200 tw:h-[36px] tw:w-[36px] tw:flex tw:items-center tw:justify-center"
            @click="switchView"
          >
            <GraphIcon class="tw:size-6" />
          </button>
        </div>
      </div>

      <div class="tw:flex tw:items-center tw:justify-between" v-if="id === 'impressions'">
        <p class="tw:text-sm tw:font-bold">
          {{ $t('rc_performance_total_impressions') }}
        </p>
        <p class="tw:font-bold tw:text-[32px]">
          {{
            total > 0
              ? new Intl.NumberFormat(language, {
                  maximumFractionDigits: 2,
                  minimumFractionDigits: 0,
                }).format(total)
              : '-'
          }}
        </p>
      </div>
    </div>

    <!-- Bottom Section: White Background -->
    <div class="tw:bg-white tw:p-4 tw:rounded-b-xl tw:flex tw:grow">
      <!-- Has RateConnect AND has data - Clickable link -->
      <router-link
        v-if="!noFilterData && hasRateConnect"
        @click="triggerEvent"
        :to="{
          name: id === 'impressions' ? 'rate-connect-running-performance' : 'rate-connect-running-booking-details',
        }"
        class="tw:group tw:flex tw:w-full tw:justify-between tw:hover:cursor-pointer tw:no-underline tw:text-inherit tw:transition-colors"
      >
        <p class="tw:text-sm tw:flex tw:items-center tw:gap-2">
          <span
            class="tw:h-2 tw:w-2 tw:flex tw:rounded-full"
            :class="!hasRateConnect && !noFilterData ? 'tw:bg-gray-500' : 'tw:bg-blue-700'"
          />{{ $t('rc_performance_website_rates') }}
        </p>

        <div class="tw:flex tw:gap-x-1 tw:items-center">
          <p class="tw:text-2xl">{{ value > 0 ? formattedValue : '-' }}</p>
          <div class="tw:rounded tw:p-1 tw:transition-colors tw:group-hover:bg-gray-200">
            <ChevronRight class="tw:size-6" />
          </div>
        </div>
      </router-link>

      <!-- No RateConnect OR no data - Non-clickable with locker or value -->
      <div
        v-else
        class="tw:group tw:flex tw:w-full tw:justify-between tw:text-inherit tw:transition-colors"
        :class="!hasRateConnect && !noFilterData ? 'tw:hover:cursor-pointer' : ''"
        @click="!hasRateConnect && !noFilterData ? openPopupFn() : null"
      >
        <p class="tw:text-sm tw:flex tw:items-center tw:gap-2">
          <span
            class="tw:h-2 tw:w-2 tw:flex tw:rounded-full"
            :class="!hasRateConnect && !noFilterData ? 'tw:bg-gray-500' : 'tw:bg-blue-700'"
          />{{ $t('rc_performance_website_rates') }}
        </p>

        <div class="tw:flex tw:gap-x-1 tw:items-center">
          <!-- Show locker when no RateConnect but has data -->
          <div
            v-if="!hasRateConnect && !noFilterData"
            class="tw:rounded tw:p-1 tw:transition-colors tw:group-hover:bg-gray-200 tw:flex tw:items-center tw:justify-center tw:h-[32px] tw:w-[32px] tw:self-center tw:flex-shrink-0"
          >
            <Locker
              class="tw:block tw:group-hover:hidden tw:flex tw:items-center tw:justify-center tw:w-[14px] tw:h-[18px]"
            />
            <LockerOpen
              class="tw:hidden tw:group-hover:block tw:flex tw:items-center tw:justify-center tw:w-[14px] tw:h-[18px]"
            />
          </div>

          <!-- Show value when no data or other cases -->
          <p v-else class="tw:text-2xl">{{ value > 0 ? formattedValue : '-' }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Graph View -->
  <div
    class="tw:flex tw:flex-col tw:rounded-xl tw:min-h-full tw:border tw:border-gray-300 tw:p-4"
    v-if="currentView === 'graph'"
  >
    <div class="tw:flex tw:items-center tw:justify-between">
      <h3 class="tw:font-bold tw:text-xl">{{ $t(title) }}</h3>
      <button class="tw:hover:cursor-pointer" @click="switchView">
        <Close class="tw:fill-black tw:size-3.5" />
      </button>
    </div>

    <ImpressionsGraph
      containerId="impressions-graph"
      :colors="['#0088D9', '#FF932C']"
      :series="chartDataSeries"
      :total="total"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'
import ChevronRight from '@/components/icons/ChevronRight.svg?component'
import GraphIcon from '@/components/icons/GraphIcon.svg?component'
import Close from '@/components/icons/Close.svg?component'
import ImpressionsGraph from '@/components/homePage/performance/ImpressionsGraph.vue'
import Locker from '@/components/icons/Locker.svg?component'
import LockerOpen from '@/components/icons/LockerOpen.svg?component'

import appAnalytics from '@/utils/tracking'

import { selectedAccommodationId } from '@/layouts/queries'
import { useEligibilityPopup } from '@/components/homePage/performance/useEligibilityPopup'
import { useFormatting } from '@/composables/useFormatting'

const props = defineProps({
  id: String,
  title: String,
  hint: String,
  value: Number,
  progression: Number,
  total: Number,
  noFilterData: Boolean,
  hasRateConnect: Boolean,
  isRateConnectEligible: Boolean,
})

const { t } = useI18n()
const currentView = ref('data')
const { openLockedMetricPopup } = useEligibilityPopup()
const { formatNumber, formatCurrency, formatPercentage } = useFormatting()

const icons = {
  impressions: new URL(`../../../assets/img/homepage/EyeIcon.png`, import.meta.url),
  bookings: new URL(`../../../assets/img/homepage/BookingIcon.png`, import.meta.url),
  revenue: new URL(`../../../assets/img/homepage/RateConnectIcon.png`, import.meta.url),
}

const comparisonText = computed(() => {
  if (props.id === 'impressions') return 'rc_performance_impressions_benchmark'
  else if (props.id === 'revenue') return 'rc_performance_revenue_benchmark'
  else if (props.id === 'bookings') return 'rc_performance_bookings_benchmark'
  else return ''
})

const switchView = () => {
  currentView.value = currentView.value === 'data' ? 'graph' : 'data'
  appAnalytics.track(appAnalytics.events.HOMEPAGE_CARDS_SHOW_CHART, {
    itemId: selectedAccommodationId.value,
    cardType: 'impressions',
  })
}

const formattedValue = computed(() => {
  if (isNaN(props.value)) return '-'
  if (props.id === 'revenue') {
    return formatCurrency(props.value)
  } else {
    return formatNumber(props.value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })
  }
})

const chartDataSeries = computed(() => {
  if (!props.total || props.total === 0) return []

  const impressedPercentage = ((props.value / props.total) * 100).toFixed(1)
  const notImpressedPercentage = (((props.total - props.value) / props.total) * 100).toFixed(1)

  const formattedImpressedPercent = formatPercentage(impressedPercentage, 0, 1)
  const formattedNotImpressedPercent = formatPercentage(notImpressedPercentage, 0, 1)

  return [
    {
      name: t('rc_chart_impressions', { ClicksPercent: formattedImpressedPercent }),
      y: Number(impressedPercentage),
    },
    {
      name: t('rc_chart_no_impressions', { ClicksPercent: formattedNotImpressedPercent }),
      y: Number(notImpressedPercentage),
    },
  ]
})

const eventsName = {
  impressions: 'HOMEPAGE_RATE_CONNECT_IMPRESSIONS_CLICKED',
  bookings: 'HOMEPAGE_RATE_CONNECT_BOOKINGS_CLICKED',
  revenue: 'HOMEPAGE_RATE_CONNECT_REVENUE_CLICKED',
}

const triggerEvent = () => {
  appAnalytics.track(appAnalytics.events[eventsName[props.id]], {
    accommodationId: selectedAccommodationId.value,
  })
}

const openPopupFn = () => {
  openLockedMetricPopup(props.isRateConnectEligible)
  appAnalytics.track(appAnalytics.events.HOMEPAGE_LOCK_ICON_CLICKED, {
    itemId: selectedAccommodationId.value,
    cardType: 'impressions',
  })
}
</script>
